import { Bot, AlertTriangle, CheckCircle2 } from 'lucide-react'

export interface FunnelStageStepperProps {
  currentStage?: string
  checkoutReadiness?: number
  frustrationScore?: number
  dominantIntent?: string
}

interface StageDefinition {
  id: string
  label: string
  aliases: string[]
}

export const FUNNEL_STAGES: StageDefinition[] = [
  { id: 'discovery', label: 'Exploración', aliases: ['discovery', 'greeting'] },
  { id: 'qualification', label: 'Calificación', aliases: ['qualification'] },
  { id: 'presentation', label: 'Presentación', aliases: ['recommendation', 'product_presentation', 'presentation'] },
  { id: 'negotiation', label: 'Negociación', aliases: ['price_discussion', 'negotiation'] },
  { id: 'objection', label: 'Objeción', aliases: ['objection_handling', 'objection'] },
  { id: 'checkout', label: 'Cierre', aliases: ['purchase_intent', 'closing', 'checkout', 'post_sale', 'support'] },
]

export function getStageIndex(stage?: string): number {
  if (!stage) return 0
  const normalized = stage.toLowerCase().trim()
  const foundIndex = FUNNEL_STAGES.findIndex((s) => s.aliases.includes(normalized) || s.id === normalized)
  return foundIndex !== -1 ? foundIndex : 0
}

export function FunnelStageStepper({
  currentStage,
  checkoutReadiness = 0,
  frustrationScore = 0,
  dominantIntent,
}: FunnelStageStepperProps) {
  const activeIndex = getStageIndex(currentStage)
  const readinessPercent = Math.min(100, Math.max(0, Math.round((checkoutReadiness || 0) * 100)))

  const getReadinessColor = (percent: number) => {
    if (percent >= 75) return 'text-emerald-700 bg-emerald-50 border-emerald-200'
    if (percent >= 40) return 'text-amber-700 bg-amber-50 border-amber-200'
    return 'text-slate-600 bg-slate-50 border-slate-200'
  }

  return (
    <div
      role="region"
      aria-label="Progreso del embudo de ventas"
      className="rounded-2xl border border-emerald-200/90 bg-gradient-to-r from-emerald-50/70 via-teal-50/40 to-sky-50/60 p-3 sm:p-3.5 shadow-2xs"
    >
      {/* Top row: Stage name, Intent, Readiness, Frustration */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-bold text-emerald-800 shadow-2xs border border-emerald-200">
            <Bot className="h-3.5 w-3.5 text-emerald-600 animate-pulse" />
            <span className="text-[11px] uppercase tracking-wider text-emerald-700/80">Embudo:</span>
            <span className="font-extrabold uppercase">{FUNNEL_STAGES[activeIndex].label}</span>
          </span>

          {dominantIntent && (
            <span className="rounded-md border border-slate-200 bg-white/80 px-2 py-0.5 text-[11px] font-medium text-slate-700">
              Intención: <span className="font-semibold">{dominantIntent}</span>
            </span>
          )}

          {frustrationScore > 0 && (
            <span
              className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold border ${
                frustrationScore >= 3
                  ? 'border-rose-300 bg-rose-100 text-rose-800 animate-pulse'
                  : 'border-amber-300 bg-amber-100 text-amber-800'
              }`}
            >
              <AlertTriangle className="h-3 w-3" />
              <span>Frustración: {frustrationScore}</span>
            </span>
          )}
        </div>

        {/* Readiness Meter */}
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold border ${getReadinessColor(
              readinessPercent
            )}`}
          >
            {readinessPercent >= 75 && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
            <span>Disposición de compra: {readinessPercent}%</span>
          </span>
        </div>
      </div>

      {/* Stepper progress dots / line */}
      <div className="mt-3 hidden sm:block">
        <div className="flex items-center justify-between gap-1">
          {FUNNEL_STAGES.map((stage, idx) => {
            const isCompleted = idx < activeIndex
            const isCurrent = idx === activeIndex
            return (
              <div key={stage.id} className="flex-1 flex flex-col items-center">
                <div className="flex items-center w-full">
                  <div
                    className={`h-1.5 w-full rounded-full transition-all ${
                      idx === 0
                        ? 'rounded-l-full'
                        : isCompleted || isCurrent
                        ? 'bg-emerald-500'
                        : 'bg-emerald-200/60'
                    }`}
                  />
                </div>
                <span
                  className={`mt-1.5 text-[10px] font-medium text-center truncate ${
                    isCurrent
                      ? 'font-bold text-emerald-900 scale-105'
                      : isCompleted
                      ? 'text-emerald-700'
                      : 'text-slate-400'
                  }`}
                >
                  {stage.label}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
